import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/firebase';
import { trackGtagEvent, ANALYTICS_EVENTS } from '@/utils/Analytics';
import { isInternalTraffic } from '@/utils/internalTraffic';
import { getLeadAttribution } from '@/utils/emailAttribution';
import { callWithTimeout } from '@/utils/callWithTimeout';

/**
 * /exam-report — "Tell us what your exam was actually like."
 *
 * WHY THIS PAGE EXISTS: the GEO citation test (2026-08-27) found that the answer
 * engines have no first-hand accounts of the 2026 ASWB format. Ten sites restate
 * ASWB's press release and PREDICT what the new exam feels like; one engine said
 * outright that its results "don't include detailed personal test taker accounts."
 * The only durable citation moat is primary data, so this page collects it.
 *
 * The questions are chosen to produce CITABLE STRUCTURE, not vibes: each closed
 * question maps to a claim a competitor can only speculate about (pacing at 122
 * questions, where the break lands, whether three choices feel easier, how heavy
 * ethics felt). The three free-text boxes are the quotes.
 *
 * THE RULES OF THE ROOM, stated in the UI and enforced in the handler:
 *   - No account. No name field. Email is OPTIONAL and the form submits without it.
 *   - Pass/not-yet is optional and only ever reported as a group number.
 *   - Quoting requires an explicit, default-unchecked opt-in, and quotes are
 *     anonymous (license level + test month, never a name).
 *
 * Phone-first: the submit control is a fixed bottom bar on phones, so it is on
 * screen at 390x844 from the first paint instead of ten scrolls down. It becomes
 * an in-flow button at sm+ where the whole form fits.
 */

const SUBMIT_TIMEOUT_MS = 15000;

// The .pen "ledge": a hard offset shadow, never a gradient.
const LEDGE: React.CSSProperties = { boxShadow: '6px 6px 0 #2A2724' };
const BUTTON_LEDGE: React.CSSProperties = { boxShadow: '0 4px 0 #1B3A38' };

type Choice = { value: string; label: string };

const EXAM_LEVELS: Choice[] = [
  { value: 'LBSW', label: 'LBSW (Bachelors)' },
  { value: 'LMSW', label: 'LMSW (Masters)' },
  { value: 'LCSW', label: 'LCSW (Clinical)' },
];

const OUTCOMES: Choice[] = [
  { value: 'passed', label: 'I passed' },
  { value: 'not_yet', label: 'Not yet' },
  { value: '', label: 'Rather not say' },
];

const TIME_PRESSURE: Choice[] = [
  { value: 'ran_out', label: 'Yes, I ran out' },
  { value: 'close', label: 'Close, I just finished' },
  { value: 'had_time', label: 'No, I had time left' },
];

const BREAK_TIMING: Choice[] = [
  { value: 'earlier', label: 'Earlier than I expected' },
  { value: 'as_expected', label: 'About where I expected' },
  { value: 'later', label: 'Later than I expected' },
  { value: 'unsure', label: "I don't remember" },
];

const THREE_CHOICE: Choice[] = [
  { value: 'easier', label: 'Easier' },
  { value: 'same', label: 'About the same' },
  { value: 'harder', label: 'Harder' },
  { value: 'unsure', label: 'Hard to say' },
];

const ETHICS_SHARE: Choice[] = [
  { value: 'under_10', label: 'Under 10%' },
  { value: '10_to_20', label: 'About 10 to 20%' },
  { value: '20_to_30', label: 'About 20 to 30%' },
  { value: 'over_30', label: 'More than 30%' },
  { value: 'unsure', label: 'No idea' },
];

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/**
 * A chip-styled single-choice group built on NATIVE radio inputs (visually
 * hidden, styled through `peer-checked`). Hand-rolled buttons would have meant
 * hand-rolling arrow-key and screen-reader behavior; this gets both for free.
 */
const ChoiceGroup: React.FC<{
  name: string;
  legend: string;
  help?: string;
  choices: Choice[];
  value: string;
  onChange: (v: string) => void;
}> = ({ name, legend, help, choices, value, onChange }) => (
  <fieldset className="mb-6">
    <legend className="text-base font-bold text-neutral-900 mb-1">{legend}</legend>
    {help && <p className="text-sm text-neutral-600 mb-2">{help}</p>}
    <div className="flex flex-wrap gap-2 mt-2">
      {choices.map((c) => (
        <label
          key={`${name}-${c.value}`}
          className="cursor-pointer"
        >
          <input
            type="radio"
            name={name}
            value={c.value}
            checked={value === c.value}
            onChange={() => onChange(c.value)}
            className="sr-only peer"
          />
          <span className="block px-3.5 py-2.5 rounded-xl border-2 border-neutral-300 bg-white text-sm font-semibold text-neutral-700 transition-colors peer-checked:border-primary-600 peer-checked:bg-primary-600 peer-checked:text-white peer-focus-visible:ring-2 peer-focus-visible:ring-primary-400 peer-focus-visible:ring-offset-2">
            {c.label}
          </span>
        </label>
      ))}
    </div>
  </fieldset>
);

const TextQuestion: React.FC<{
  id: string;
  label: string;
  help?: string;
  placeholder: string;
  maxLength: number;
  value: string;
  onChange: (v: string) => void;
}> = ({ id, label, help, placeholder, maxLength, value, onChange }) => (
  <div className="mb-6">
    <label htmlFor={id} className="block text-base font-bold text-neutral-900 mb-1">{label}</label>
    {help && <p className="text-sm text-neutral-600 mb-2">{help}</p>}
    <textarea
      id={id}
      rows={3}
      maxLength={maxLength}
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-4 py-3 rounded-xl border-2 border-neutral-300 bg-white focus:border-primary-500 focus:outline-none text-neutral-800"
    />
  </div>
);

const ExamReport: React.FC = () => {
  const now = new Date();
  const [examLevel, setExamLevel] = useState('');
  const [testMonth, setTestMonth] = useState(String(now.getMonth() + 1));
  const [testYear, setTestYear] = useState(String(Math.max(2026, now.getFullYear())));
  const [outcome, setOutcome] = useState('');
  const [timePressure, setTimePressure] = useState('');
  const [breakTiming, setBreakTiming] = useState('');
  const [threeChoiceFeel, setThreeChoiceFeel] = useState('');
  const [ethicsShare, setEthicsShare] = useState('');
  const [surprised, setSurprised] = useState('');
  const [studyDifferently, setStudyDifferently] = useState('');
  const [anythingElse, setAnythingElse] = useState('');
  const [email, setEmail] = useState('');
  const [quoteConsent, setQuoteConsent] = useState(false);
  const [state, setState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');

  const viewedRef = useRef(false);
  const startedRef = useRef(false);

  // Only the exam and the month/year are required: those two are what make an
  // answer aggregatable. Everything else can be skipped and still counts.
  const canSubmit = examLevel !== '' && testMonth !== '' && testYear !== '';

  const fire = useCallback((name: string, extra: Record<string, unknown> = {}) => {
    if (!isInternalTraffic()) trackGtagEvent(name, extra);
  }, []);

  useEffect(() => {
    if (viewedRef.current) return;
    viewedRef.current = true;
    // `src` is the internal entry tag (e.g. ?src=diagnostic_report), NOT a utm_*,
    // so it can't clobber the stored 90-day acquisition record. It is the only way
    // to tell "came from the diagnostic report" apart from direct traffic.
    const src = new URLSearchParams(window.location.search).get('src');
    fire(ANALYTICS_EVENTS.EXAM_REPORT_VIEWED, { internal_src: src ?? undefined });
  }, [fire]);

  /** First touch of ANY field is the "started" signal — that is the drop-off we care about. */
  const markStarted = useCallback(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    fire(ANALYTICS_EVENTS.EXAM_REPORT_STARTED);
  }, [fire]);

  const bind = useCallback(
    <T,>(setter: (v: T) => void) => (v: T) => { markStarted(); setter(v); },
    [markStarted],
  );

  const years = useMemo(() => {
    const max = Math.max(2026, now.getFullYear());
    const out: number[] = [];
    for (let y = 2026; y <= max; y += 1) out.push(y);
    return out;
  }, [now]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (state === 'sending' || !canSubmit) return;
    setState('sending');
    try {
      const attribution = getLeadAttribution();
      await callWithTimeout(
        () => httpsCallable(functions, 'submitExamReport')({
          examLevel,
          testMonth: Number(testMonth),
          testYear: Number(testYear),
          // Empty string means "not answered" everywhere in this form. Send
          // undefined so the backend stores null instead of an empty enum.
          outcome: outcome || undefined,
          timePressure: timePressure || undefined,
          breakTiming: breakTiming || undefined,
          threeChoiceFeel: threeChoiceFeel || undefined,
          ethicsShare: ethicsShare || undefined,
          surprised: surprised || undefined,
          studyDifferently: studyDifferently || undefined,
          anythingElse: anythingElse || undefined,
          // The whole point: this is allowed to be absent.
          email: email.trim() || undefined,
          emailConsent: Boolean(email.trim()),
          quoteConsent,
          ...attribution,
        }),
        { timeoutMs: SUBMIT_TIMEOUT_MS, retries: 0 },
      );
      fire(ANALYTICS_EVENTS.EXAM_REPORT_SUBMITTED, {
        exam_level: examLevel,
        gave_email: Boolean(email.trim()),
        quote_consent: quoteConsent,
      });
      setState('sent');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch {
      setState('error');
    }
  };

  const head = (
    <Helmet>
      <title>What was your 2026 ASWB exam actually like? · Social Work Test Prep Academy</title>
      <meta
        name="description"
        content="Took the ASWB exam in the 2026 format? Tell us what it was actually like: pacing at 122 questions, where the break landed, how three answer choices felt, and how heavy ethics was. Two minutes, no account, email optional."
      />
      <link rel="canonical" href="https://www.socialworktestprepacademy.com/exam-report" />
      <meta property="og:title" content="What was your 2026 ASWB exam actually like?" />
      <meta property="og:description" content="Every prep site is predicting what the new exam feels like. Nobody asked the people who sat it. Two minutes, no account, email optional." />
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://www.socialworktestprepacademy.com/exam-report" />
      <meta name="twitter:card" content="summary" />
    </Helmet>
  );

  if (state === 'sent') {
    return (
      <main className="min-h-screen bg-[#FAF6EF] px-4 py-10">
        {head}
        <div className="max-w-xl mx-auto bg-white border-2 border-neutral-900 rounded-2xl p-7 text-center" style={LEDGE}>
          <h1 className="font-display font-bold text-2xl text-neutral-900 mb-3">Thank you. That is genuinely useful.</h1>
          <p className="text-neutral-700 mb-4">
            Yours is now part of the only set of first-hand accounts of the 2026 format anyone is
            collecting. When there are enough reports to say something honest, we publish the
            numbers and the method, free, for everyone studying.
          </p>
          <p className="text-sm text-neutral-600 mb-6">
            Know someone else who has already tested? They can add theirs at
            socialworktestprepacademy.com/exam-report.
          </p>
          <Link
            to="/try?src=exam_report"
            className="inline-block px-6 py-3 rounded-xl bg-primary-600 hover:bg-primary-700 text-white font-bold"
            style={BUTTON_LEDGE}
          >
            See the free diagnostic
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#FAF6EF] px-4 py-6 sm:py-10 pb-32 sm:pb-10">
      {head}
      <div className="max-w-xl mx-auto">
        <h1 className="font-display font-bold text-2xl sm:text-3xl text-neutral-900 mb-2">
          Tell us what your exam was actually like
        </h1>
        <p className="text-neutral-700 mb-5">
          Every prep site is <em>predicting</em> how the 2026 format feels. Nobody has asked the
          people who sat it. Two minutes, no account, and your email is optional. We publish the
          results as a free report so the next person walks in knowing.
        </p>

        <form onSubmit={submit}>
          <div className="bg-white border-2 border-neutral-900 rounded-2xl p-5 sm:p-7 mb-5" style={LEDGE}>
            <ChoiceGroup
              name="examLevel"
              legend="Which exam did you take?"
              choices={EXAM_LEVELS}
              value={examLevel}
              onChange={bind(setExamLevel)}
            />

            <fieldset className="mb-6">
              <legend className="text-base font-bold text-neutral-900 mb-2">When did you take it?</legend>
              <div className="flex gap-2">
                <select
                  aria-label="Test month"
                  value={testMonth}
                  onChange={(e) => { markStarted(); setTestMonth(e.target.value); }}
                  className="flex-1 px-3 py-3 rounded-xl border-2 border-neutral-300 bg-white font-semibold text-neutral-800 focus:border-primary-500 focus:outline-none"
                >
                  {MONTHS.map((m, i) => <option key={m} value={String(i + 1)}>{m}</option>)}
                </select>
                <select
                  aria-label="Test year"
                  value={testYear}
                  onChange={(e) => { markStarted(); setTestYear(e.target.value); }}
                  className="w-32 px-3 py-3 rounded-xl border-2 border-neutral-300 bg-white font-semibold text-neutral-800 focus:border-primary-500 focus:outline-none"
                >
                  {years.map((y) => <option key={y} value={String(y)}>{y}</option>)}
                </select>
              </div>
            </fieldset>

            <ChoiceGroup
              name="outcome"
              legend="How did it go?"
              help="Optional, and you can skip it. We only ever report this as a group number, never beside your words."
              choices={OUTCOMES}
              value={outcome}
              onChange={bind(setOutcome)}
            />
          </div>

          <div className="bg-white border-2 border-neutral-900 rounded-2xl p-5 sm:p-7 mb-5" style={LEDGE}>
            <p className="text-sm font-bold uppercase tracking-wide text-accent-600 mb-4">In the room</p>

            <ChoiceGroup
              name="timePressure"
              legend="Did you run out of time?"
              choices={TIME_PRESSURE}
              value={timePressure}
              onChange={bind(setTimePressure)}
            />

            <ChoiceGroup
              name="breakTiming"
              legend="Where did the break land compared to what you expected?"
              choices={BREAK_TIMING}
              value={breakTiming}
              onChange={bind(setBreakTiming)}
            />

            <ChoiceGroup
              name="threeChoiceFeel"
              legend="Three answer choices instead of four. Compared to your practice, did that feel:"
              choices={THREE_CHOICE}
              value={threeChoiceFeel}
              onChange={bind(setThreeChoiceFeel)}
            />

            <ChoiceGroup
              name="ethicsShare"
              legend="Roughly what share of the exam felt like ethics questions?"
              help="A rough sense is fine. Nobody counted."
              choices={ETHICS_SHARE}
              value={ethicsShare}
              onChange={bind(setEthicsShare)}
            />

            <TextQuestion
              id="surprised"
              label="What surprised you most?"
              placeholder="The thing you did not see coming."
              maxLength={600}
              value={surprised}
              onChange={bind(setSurprised)}
            />

            <TextQuestion
              id="study-differently"
              label="What would you study differently?"
              placeholder="If you could tell yourself one thing eight weeks out."
              maxLength={600}
              value={studyDifferently}
              onChange={bind(setStudyDifferently)}
            />

            <TextQuestion
              id="anything-else"
              label="Anything else we should know?"
              placeholder="Optional."
              maxLength={1000}
              value={anythingElse}
              onChange={bind(setAnythingElse)}
            />
          </div>

          <div className="bg-white border-2 border-neutral-900 rounded-2xl p-5 sm:p-7 mb-5" style={LEDGE}>
            <label htmlFor="exam-report-email" className="block text-base font-bold text-neutral-900 mb-1">
              Want the finished report? Leave your email.
            </label>
            <p className="text-sm text-neutral-600 mb-2">
              Optional. If you give it, we may email you the report and occasional study tips, and
              you can unsubscribe anytime. Leave it blank and your report still counts. We never ask
              for your name.
            </p>
            <input
              id="exam-report-email"
              type="email"
              value={email}
              autoComplete="email"
              placeholder="you@email.com (optional)"
              onChange={(e) => { markStarted(); setEmail(e.target.value); }}
              className="w-full px-4 py-3 rounded-xl border-2 border-neutral-300 bg-white focus:border-primary-500 focus:outline-none text-neutral-800"
            />

            <label className="flex items-start gap-3 mt-5 cursor-pointer">
              <input
                type="checkbox"
                checked={quoteConsent}
                onChange={(e) => { markStarted(); setQuoteConsent(e.target.checked); }}
                className="mt-1 w-5 h-5 flex-shrink-0 accent-primary-600"
              />
              <span className="text-sm text-neutral-700">
                You may quote what I wrote, anonymously, in the published report. A quote would show
                my license level and test month, never my name or email. Leave this unticked and my
                answers are only ever counted, never quoted.
              </span>
            </label>
          </div>

          {state === 'error' && (
            <p className="text-sm text-red-700 font-semibold mb-3" role="alert">
              That did not go through. Please try again.
            </p>
          )}

          {/* Phone-first: fixed to the bottom of the viewport so the submit is on
              screen at 390x844 without scrolling, and in normal flow from sm up. */}
          <div className="fixed inset-x-0 bottom-0 z-40 px-4 py-3 bg-[#FAF6EF] border-t-2 border-neutral-900 sm:static sm:p-0 sm:bg-transparent sm:border-0">
            <button
              type="submit"
              data-testid="exam-report-submit"
              disabled={state === 'sending' || !canSubmit}
              className="w-full px-6 py-4 rounded-xl bg-primary-600 hover:bg-primary-700 text-white font-bold text-lg disabled:opacity-50"
              style={BUTTON_LEDGE}
            >
              {state === 'sending' ? 'Sending…' : 'Send my report'}
            </button>
            <p className="text-xs text-neutral-600 text-center mt-2">
              {canSubmit ? 'No account, no email required.' : 'Pick your exam and test date to send.'}
            </p>
          </div>
        </form>
      </div>
    </main>
  );
};

export default ExamReport;
